import { ProgramType } from '@newfront-insurance/account-api';
import { hasAnyAccountViewAccessScopes } from '@newfront-insurance/admin-ui/layout';
import { AM_ROLES, AuthRole, PRODUCER_ROLES } from '@newfront-insurance/auth-api';
import { usePermission as usePermissionNextAuth } from '@newfront-insurance/next-auth';
import { useProvider } from '@newfront-insurance/react-provision';

import { useIsOwnedByCurrentUser } from './use-is-owned-by-current-user';
import type { DetailedAccount } from '../features/account-details/queries/use-detailed-account';
import { TRPCProvider } from '../providers/trpc';

import type { WithEmployeeRoleMappings } from '@/api/types';
import { AuthProvider } from '@/client/providers/auth';

interface UsePermissionOptions {
  hasAll?: boolean;
  scopes: AuthRole[];
}

export function usePermission(opts: UsePermissionOptions): boolean {
  return usePermissionNextAuth(AuthProvider, opts);
}

export function useCanViewFullAccount(account: WithEmployeeRoleMappings | undefined): boolean {
  const isOwnedByCurrentUser = useIsOwnedByCurrentUser(account);

  const hasAnyAccess = usePermission({
    scopes: hasAnyAccountViewAccessScopes,
  });

  return isOwnedByCurrentUser || hasAnyAccess;
}

export function useCanCreateBilling(): boolean {
  return usePermission({
    scopes: [AuthRole.ACCOUNT_MANAGER, AuthRole.CSA, AuthRole.ENGINEER, AuthRole.OPERATIONS],
  });
}

export function useCanBulkImportServicingTeams(): boolean {
  return usePermission({
    scopes: [AuthRole.SERVICE_OPS, AuthRole.AM_MANAGER, AuthRole.PRODUCER_MANAGER, AuthRole.ENGINEER],
  });
}

export function useCanManageFollowupDates(): boolean {
  return usePermission({
    scopes: [AuthRole.PRODUCER, AuthRole.ENGINEER],
  });
}

export function useCanManageContacts(account: WithEmployeeRoleMappings): boolean {
  const hasAdminAccess = usePermission({
    scopes: [
      AuthRole.SERVICE_OPS,
      AuthRole.AM_MANAGER,
      AuthRole.CLIENT_SERVICE_TEAM_ADMIN,
      AuthRole.PRODUCER_MANAGER,
      AuthRole.SALES_MANAGER,
      AuthRole.CONVERSION,
      AuthRole.ENGINEER,
      AuthRole.RISK_MANAGER,
      AuthRole.INSURANCE_OPS,
    ],
  });
  const hasOwnershipAccess = useHasOwnershipAccess(account, [
    AuthRole.ACCOUNT_MANAGER,
    AuthRole.CSA,
    AuthRole.PRODUCER,
  ]);

  return hasAdminAccess || hasOwnershipAccess;
}

export function useCanLoadAccountSummariesDashboard(): boolean {
  return usePermission({
    scopes: [AuthRole.ACCOUNT_MANAGER, AuthRole.CSA, AuthRole.ENGINEER, AuthRole.PRODUCER],
  });
}

export function useCanDownloadProofOfInsurance(): boolean {
  return usePermission({
    scopes: [AuthRole.ACCOUNT_MANAGER, AuthRole.CSA, AuthRole.ENGINEER, AuthRole.OPERATIONS],
  });
}

export function useCanCreateCommercialAccount(): boolean {
  return usePermission({
    scopes: [AuthRole.ENGINEER, AuthRole.SERVICE_OPS],
  });
}

export function useCanCreatePersonalAccount(): boolean {
  return usePermission({
    scopes: [
      AuthRole.ENGINEER,
      AuthRole.PL_ACCOUNT_MANAGER,
      AuthRole.PL_PRODUCER,
      AuthRole.AM_MANAGER,
      AuthRole.PRODUCER_MANAGER,
      AuthRole.GROWTH_CALLER,
      AuthRole.CONVERSION,
      AuthRole.SERVICE_OPS,
    ],
  });
}

export function useCanSelectProducer(): boolean {
  return usePermission({
    scopes: [AuthRole.ENGINEER, AuthRole.PRODUCER_MANAGER, AuthRole.SALES_MANAGER, AuthRole.RISK_MANAGER],
  });
}

/**
 * @deprecated the use of this hook is discouraged. Prefer defining your feature-specific hook for
 * the roles that you need. This hook is going to be removed soon because the definition of what an
 * admin is is going to change.
 */
export function useIsLegacyAdmin(): boolean {
  return usePermission({
    scopes: [AuthRole.ACCOUNT_MANAGER, AuthRole.CSA, AuthRole.ENGINEER, AuthRole.OPERATIONS, AuthRole.SALES_MANAGER],
  });
}

export function useCanManageLinesOfCoverage(): boolean {
  const canManageLinesOfCoverage = usePermission({
    scopes: [AuthRole.ACCOUNT_MANAGER, AuthRole.CSA, AuthRole.OPERATIONS, AuthRole.ENGINEER],
  });
  return canManageLinesOfCoverage;
}

export function useIsAccountManager(): boolean {
  return usePermission({ scopes: [AuthRole.ACCOUNT_MANAGER] });
}

export function useIsAccountManagerManager(): boolean {
  return usePermission({ scopes: [AuthRole.AM_MANAGER] });
}

export function useIsClAccountManager(): boolean {
  return usePermission({ scopes: [AuthRole.CL_ACCOUNT_MANAGER] });
}

export function useIsPlAccountManager(): boolean {
  return usePermission({ scopes: [AuthRole.PL_ACCOUNT_MANAGER] });
}

export function useIsClientServiceTeamAdmin(): boolean {
  return usePermission({ scopes: [AuthRole.CLIENT_SERVICE_TEAM_ADMIN] });
}

export function useCanManageAnyAccountManagementTeamMember(): boolean {
  return usePermission({
    scopes: [
      AuthRole.ACCOUNT_MANAGER,
      AuthRole.AM_MANAGER,
      AuthRole.CLIENT_SERVICE_TEAM_ADMIN,
      AuthRole.CONVERSION,
      AuthRole.CSA,
      AuthRole.ENGINEER,
      AuthRole.PRODUCER_MANAGER,
      AuthRole.SALES_MANAGER,
      AuthRole.SERVICE_OPS,
    ],
  });
}

export function useIsProducer(): boolean {
  return usePermission({ scopes: [AuthRole.PRODUCER] });
}

export function useCanManageAnyProducerTeamMember(): boolean {
  return usePermission({
    scopes: [
      AuthRole.ACCOUNT_MANAGER,
      AuthRole.AM_MANAGER,
      AuthRole.CSA,
      AuthRole.ENGINEER,
      AuthRole.PRODUCER_MANAGER,
      AuthRole.SALES_MANAGER,
      AuthRole.SERVICE_OPS,
    ],
  });
}

export function useCanManageAnyClientServiceAssociate(): boolean {
  return usePermission({
    scopes: [
      AuthRole.ACCOUNT_MANAGER,
      AuthRole.AM_MANAGER,
      AuthRole.CLIENT_SERVICE_TEAM_ADMIN,
      AuthRole.CONVERSION,
      AuthRole.CSA,
      AuthRole.ENGINEER,
      AuthRole.PRODUCER_MANAGER,
      AuthRole.SALES_MANAGER,
      AuthRole.SERVICE_OPS,
    ],
  });
}

export function useCanManageAnyClaimTeamMember(): boolean {
  return usePermission({
    scopes: [AuthRole.ENGINEER, AuthRole.INSURANCE_OPS, AuthRole.RISK_MANAGER],
  });
}

export function useIsConversionTeamMember(): boolean {
  return usePermission({ scopes: [AuthRole.CONVERSION] });
}

export function useIsCsa(): boolean {
  return usePermission({ scopes: [AuthRole.CSA] });
}

export function useIsGrowthCaller(): boolean {
  return usePermission({ scopes: [AuthRole.GROWTH_CALLER] });
}

export function useCanSetTestAccount(): boolean {
  return usePermission({ scopes: [AuthRole.ENGINEER, AuthRole.SERVICE_OPS] });
}

export function useCanManageClaims(): boolean {
  return usePermission({
    scopes: [
      AuthRole.ACCOUNT_MANAGER,
      AuthRole.CSA,
      AuthRole.ENGINEER,
      AuthRole.RISK_MANAGER,
      AuthRole.INSURANCE_OPS,
      AuthRole.OPERATIONS,
    ],
  });
}

export function useCanGrantClientAccess(account: WithEmployeeRoleMappings | undefined): boolean {
  const hasAdminAccess = usePermission({
    scopes: [AuthRole.SERVICE_OPS, AuthRole.AM_MANAGER, AuthRole.PRODUCER_MANAGER, AuthRole.ENGINEER],
  });
  const hasOwnershipAccess = useHasOwnershipAccess(account, [
    AuthRole.ACCOUNT_MANAGER,
    AuthRole.CSA,
    AuthRole.PRODUCER,
    AuthRole.EB_ACCOUNT_MANAGER,
    AuthRole.EB_PRODUCER,
  ]);
  return hasAdminAccess || hasOwnershipAccess;
}

function useHasOwnershipAccess(account: WithEmployeeRoleMappings | undefined, scopes: AuthRole[]): boolean {
  const isOwnedByCurrentUser = useIsOwnedByCurrentUser(account);
  const hasOwnershipScopes = usePermission({ scopes });

  return isOwnedByCurrentUser && hasOwnershipScopes;
}

export function useCanManageAffiliates(account: WithEmployeeRoleMappings): boolean {
  const hasAdminAccess = usePermission({
    scopes: [
      AuthRole.AM_MANAGER,
      AuthRole.PRODUCER_MANAGER,
      AuthRole.SALES_MANAGER,
      AuthRole.SERVICE_OPS,
      AuthRole.ENGINEER,
    ],
  });
  const hasOwnershipAccess = useHasOwnershipAccess(account, [
    AuthRole.ACCOUNT_MANAGER,
    AuthRole.CSA,
    AuthRole.PRODUCER,
  ]);

  return hasAdminAccess || hasOwnershipAccess;
}

export function useCanAddAffinityGroups(account: WithEmployeeRoleMappings): boolean {
  const hasAdminAccess = usePermission({
    scopes: [AuthRole.PRODUCER_MANAGER, AuthRole.SALES_MANAGER],
  });
  const hasOwnershipAccess = useHasOwnershipAccess(account, [AuthRole.PRODUCER]);

  return hasAdminAccess || hasOwnershipAccess;
}

export function useCanRemoveAffinityGroups(): boolean {
  return usePermission({ scopes: [AuthRole.PRODUCER_MANAGER, AuthRole.SALES_MANAGER, AuthRole.ENGINEER] });
}

export function useCanUnarchiveAccount(account: WithEmployeeRoleMappings): boolean {
  const hasAdminAccess = usePermission({
    scopes: [AuthRole.AM_MANAGER, AuthRole.PRODUCER_MANAGER, AuthRole.SALES_MANAGER, AuthRole.ENGINEER],
  });
  const hasOwnershipAccess = useHasOwnershipAccess(account, [
    AuthRole.ACCOUNT_MANAGER,
    AuthRole.CSA,
    AuthRole.PRODUCER,
  ]);

  return hasAdminAccess || hasOwnershipAccess;
}

export function useCanDebugAnyUser(): boolean {
  return usePermission({ scopes: [AuthRole.ENGINEER] });
}

export function useCanManageUserEmployeeAdmin(): boolean {
  return usePermission({ scopes: [AuthRole.ENGINEER, AuthRole.WTW_USER_ADMIN] });
}

export function useCanManagePolicyTermFields(): boolean {
  return usePermission({
    scopes: [AuthRole.ACCOUNT_MANAGER, AuthRole.CSA],
  });
}

export function useCanManagePolicyStage(): boolean {
  return usePermission({
    scopes: [
      AuthRole.AM_MANAGER,
      AuthRole.BUSINESS_OPERATIONS,
      AuthRole.SERVICE_OPS,
      AuthRole.FINANCE,
      AuthRole.ENGINEER,
    ],
  });
}

export function useCanGenerateAutoIdCards(): boolean {
  return usePermission({
    scopes: [AuthRole.ENGINEER, AuthRole.ACCOUNT_MANAGER, AuthRole.CSA],
  });
}

export function useShouldAssignForceId(accountLike: { programType: ProgramType }): boolean {
  const isEb = accountLike.programType === ProgramType.EMPLOYEE_BENEFITS;
  const hasRole = usePermission({
    scopes: [AuthRole.BUSINESS_OPERATIONS, AuthRole.SERVICE_OPS, AuthRole.ENGINEER],
  });
  return isEb && hasRole;
}

export function useCanAddServicingTeamMember(): boolean {
  return usePermission({
    scopes: [
      AuthRole.SERVICE_OPS,
      AuthRole.ACCOUNT_MANAGER,
      AuthRole.AM_MANAGER,
      AuthRole.CLIENT_SERVICE_TEAM_ADMIN,
      AuthRole.CSA,
      AuthRole.PRODUCER_MANAGER,
      AuthRole.SALES_MANAGER,
      AuthRole.CONVERSION,
      AuthRole.INSURANCE_OPS,
      AuthRole.RISK_MANAGER,
      AuthRole.ENGINEER,
    ],
  });
}

export function useCanDeleteInsurableItem(): boolean {
  return usePermission({ scopes: [AuthRole.ENGINEER, AuthRole.SERVICE_OPS] });
}

export function useCanSeeContractReview(account: DetailedAccount): boolean {
  const userHasContractReviewAccess = usePermission({
    scopes: [
      ...AM_ROLES,
      ...PRODUCER_ROLES,
      AuthRole.ENGINEER,
      AuthRole.EXECUTIVE,
      AuthRole.AM_MANAGER,
      AuthRole.PRODUCER_MANAGER,
      AuthRole.INSURANCE_OPS,
    ],
  });

  const isPnC = account.programType === ProgramType.PROPERTY_AND_CASUALTY;

  return isPnC && userHasContractReviewAccess;
}

export default function useCanSeePartnersManagement(account: DetailedAccount): boolean {
  const { useQuery } = useProvider(TRPCProvider);
  const { data: clientAccessFeatures } = useQuery(['accounts.getClientAccessFeatures', { accountUuid: account.uuid }]);

  const userHasAccess = usePermission({
    scopes: [
      ...AM_ROLES,
      ...PRODUCER_ROLES,
      AuthRole.ENGINEER,
      AuthRole.EXECUTIVE,
      AuthRole.AM_MANAGER,
      AuthRole.PRODUCER_MANAGER,
      AuthRole.INSURANCE_OPS,
      AuthRole.PARTNER_MANAGEMENT_USER,
    ],
  });

  return !!clientAccessFeatures?.partnerManagement && userHasAccess;
}

export function useCanReinstatePolicy(): boolean {
  return usePermission({ scopes: [AuthRole.ENGINEER, AuthRole.SERVICE_OPS] });
}

export function useCanManageTR(): boolean {
  return usePermission({
    scopes: [
      AuthRole.ENGINEER,
      AuthRole.SERVICE_OPS,
      AuthRole.BUSINESS_OPERATIONS,
      AuthRole.EB_ACCOUNT_MANAGER,
      AuthRole.EB_PRODUCER,
      AuthRole.BPO_MANAGER,
      AuthRole.BPO_TECHTAMMINA,
      AuthRole.BPO_WORKER,
    ],
  });
}

export function useCanManageCensus(account: WithEmployeeRoleMappings | undefined): boolean {
  const isOwnedByCurrentUser = useIsOwnedByCurrentUser(account);
  const hasAdminAccess = usePermission({
    scopes: [AuthRole.ENGINEER, AuthRole.BUSINESS_OPERATIONS],
  });
  return isOwnedByCurrentUser || hasAdminAccess;
}

export const useCanSeeMobileAppMappings = () => {
  return usePermission({
    scopes: [AuthRole.ENGINEER, AuthRole.BUSINESS_OPERATIONS],
  });
};

export function useCanUpdateEmployees(): boolean {
  return usePermission({
    scopes: [AuthRole.ACCOUNT_MANAGER, AuthRole.CSA, AuthRole.ENGINEER, AuthRole.OPERATIONS, AuthRole.SALES_MANAGER],
  });
}

export function useIsEngineer(): boolean {
  return usePermission({ scopes: [AuthRole.ENGINEER] });
}

export function useIsEbProducer(): boolean {
  return usePermission({ scopes: [AuthRole.EB_PRODUCER] });
}

export function useIsEbAccountManager(): boolean {
  return usePermission({ scopes: [AuthRole.EB_ACCOUNT_MANAGER] });
}

export function useIsClProducer(): boolean {
  return usePermission({ scopes: [AuthRole.CL_PRODUCER] });
}

export function usePolicyIngestionPermissions(): boolean {
  return usePermission({
    scopes: [
      AuthRole.ACCOUNT_MANAGER,
      AuthRole.AM_MANAGER,
      AuthRole.BPO_MANAGER,
      AuthRole.BPO_TECHTAMMINA,
      AuthRole.BPO_WORKER,
      AuthRole.CL_ACCOUNT_MANAGER,
      AuthRole.CLIENT_SERVICE_TEAM_ADMIN,
      AuthRole.CSA,
      AuthRole.ENGINEER,
      AuthRole.SERVICE_OPS,
    ],
  });
}
